import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import List from '../List'

import { formatListItemValues } from '../../formatters'
import { insertIf } from '../../../helpers/common'
import { useConversionResultsColumns } from '../ListItemsTable/Columns/ConversionResultsColumns/useConversionResultsColumns'

import { selectedPartnershipSelector } from '../../../modules/selectors/partnerships'

import { listItemsTableColumnsMinWidth, listItemsTableColumnsMaxWidth } from '../ListItemsTable/columnsWidth'
import { DV_360_PLATFORM, GOOGLE_PLATFORM, TIKTOK_PLATFORM } from '../../../constants/selectLists/platformList'

import useStyles from './styles'

const ListItemsTotals = ({
  items,
  data,
  platform,
  currencySymbol,
  showPlatformColumn = true,
  hideDropdownColumn,
  showRowCheckbox
}) => {
  const classes = useStyles()

  const selectedPartnership = useSelector(selectedPartnershipSelector)

  const { spend_metrics: showAdditionalMetrics } = selectedPartnership

  // depending on platform we should dynamically hide or show columns
  const isGooglePlatform = platform === GOOGLE_PLATFORM
  const isDV360Platform = platform === DV_360_PLATFORM
  const isTikTokPlatform = platform === TIKTOK_PLATFORM

  const formattedData = useMemo(
    () =>
      data
        ? {
            data,
            ...data,
            ...formatListItemValues(data, { symbol: currencySymbol })
          }
        : [],
    [data, currencySymbol]
  )

  const isItemWithVideoViewsExist = useMemo(() => items.find(item => item['video_views']), [items])

  const conversionRelatedColumns = useConversionResultsColumns({
    items,
    currencySymbol,
    showAdditionalMetrics,
    advancedMetrics: false,
    showAttributionWindow: false // should be hidden for totals
  })

  // empty columns imitate structure of ListItemsTable component to align tables
  const totalsColumns = useMemo(
    () => [
      ...insertIf(showRowCheckbox, {
        Component: () => <div></div>,
        style: {
          maxWidth: listItemsTableColumnsMaxWidth.checkbox
        }
      }),
      ...insertIf(showPlatformColumn, {
        Component: () => '',
        style: {
          minWidth: listItemsTableColumnsMinWidth.platform,
          maxWidth: listItemsTableColumnsMaxWidth.platform,
          paddingLeft: 5,
          paddingRight: 5
        }
      }),
      {
        Component: () => <div className={classes.text}>Totals</div>,
        style: {
          minWidth: listItemsTableColumnsMinWidth.name,
          maxWidth: listItemsTableColumnsMaxWidth.name
        }
      },
      {
        Component: ({ impressions }) => <div className={classes.text}>{impressions || '-'}</div>,
        className: classes.valueColumn,
        style: {
          minWidth: listItemsTableColumnsMinWidth.impressions
        }
      },
      ...insertIf(isGooglePlatform, {
        Component: ({ active_view_impressions }) => (
          <div className={classes.text}>{active_view_impressions || '-'}</div>
        ),
        className: classes.valueColumn,
        style: {
          minWidth: listItemsTableColumnsMinWidth.activeViewImpressions
        }
      }),
      ...insertIf(!isGooglePlatform, {
        Component: () => '',
        className: classes.valueColumn,
        style: {
          minWidth: listItemsTableColumnsMinWidth.reach
        }
      }),
      {
        Component: ({ clicks }) => <div className={classes.text}>{clicks || '-'}</div>,
        className: classes.valueColumn,
        style: {
          minWidth: listItemsTableColumnsMinWidth.clicks
        }
      },
      ...insertIf(!isGooglePlatform && !isDV360Platform && !isTikTokPlatform, {
        Component: ({ engagement }) => <div className={classes.text}>{engagement || '-'}</div>,
        className: classes.valueColumn,
        style: {
          minWidth: listItemsTableColumnsMinWidth.engagements
        }
      }),
      ...insertIf(isItemWithVideoViewsExist, {
        headTitle: 'Video Views',
        Component: ({ video_views }) => <div className={classes.text}>{video_views || '-'}</div>,
        headClassName: classes.valueColumn,
        className: classes.valueColumn,
        style: {
          minWidth: listItemsTableColumnsMinWidth.videoViews
        }
      }),

      ...conversionRelatedColumns,

      ...insertIf(showAdditionalMetrics, {
        Component: ({ spend }) => <div className={classes.text}>{spend || '-'}</div>,
        className: classes.valueColumn,
        style: {
          minWidth: listItemsTableColumnsMinWidth.spend
        }
      }),
      {
        Component: () => '',
        style: {
          minWidth: listItemsTableColumnsMinWidth.status,
          maxWidth: listItemsTableColumnsMaxWidth.status,
          textOverflow: 'unset'
        }
      },
      ...insertIf(!hideDropdownColumn, {
        Component: () => '',
        style: {
          minWidth: listItemsTableColumnsMinWidth.dropdown,
          maxWidth: listItemsTableColumnsMinWidth.dropdown,
          padding: 0
        }
      })
    ],
    [
      showPlatformColumn,
      classes.valueColumn,
      classes.text,
      isGooglePlatform,
      isDV360Platform,
      isTikTokPlatform,
      isItemWithVideoViewsExist,
      conversionRelatedColumns,
      showAdditionalMetrics,
      hideDropdownColumn,
      showRowCheckbox
    ]
  )

  return <List cols={totalsColumns} listData={[formattedData]} hideHeadlineColumns />
}

ListItemsTotals.propTypes = {
  data: PropTypes.object.isRequired,
  platform: PropTypes.string,
  currencySymbol: PropTypes.string,
  showPlatformColumn: PropTypes.bool,
  hideDropdownColumn: PropTypes.bool
}

export default ListItemsTotals
